import {
    $,
    changeHash,
    createNotification,
} from "../utils/dom";
import {
    EMAIL_REGEXP,
    MIN_PHONE_NUMBER_LENGTH,
    MAX_PHONE_NUMBER_LENGTH,
} from "../utils/dom";
import { isHeicFile, convertHeicToJpeg } from "../utils/heic";
import {
    storeImage,
} from "../utils/indexdb";

import shared, { MAX_SELL_IMAGES } from "../data/js/shared";

const { unloadConfirmMessage, photoLimitSomeAdded, photoLimitNoneAdded } = shared.post;

import { VEHICLE_SUBCATS } from "../generated/static-categories";
import { trackError, ErrorSeverity } from "../utils/error-tracking";
import { asError } from '../hooks/as-error';

// Consolidated Globals
/**
 * The sell/edit UI's photo cap. Derived from MAX_SELL_IMAGES so the uploader can never
 * cut off below the number the copy promises — this was a literal 12 while the copy
 * generated from the shared constant already said 20.
 */
export const MAX_PHOTOS = MAX_SELL_IMAGES;
export const PHOTO_WIDTH = 1280;
export const IMAGE_TYPE = "image/jpeg";
export const IMAGE_COMPRESSION = 1;

// Used in post flow to reorder images
export const imageKey = (img) => {
    // Use full URL to avoid collisions from substring matching
    return img.url || img;
};

export const makeImageKeys = (images) => {
    return images.map(imageKey);
};

export const getClassForState = (label, s) => {
    if (s === "short") return `invalid STATE--${label}-short`;
    if (s === "empty") return `invalid STATE--${label}-empty`;
    if (s === "taken") return `invalid STATE--${label}-taken`;
    if (s === "maxed") return `invalid STATE--${label}-maxed`;
    if (s === "invalid") return `invalid STATE--${label}-invalid`;
    if (s === "denied") return `invalid STATE--${label}-denied`;
    if (s === "last") return `invalid STATE--${label}-last`;
    if (s === "duplicate") return `invalid STATE--${label}-duplicate`;
    if (s === "valid") return `valid`;
    return "";
};

export const getFocusFilled = (key, focus, postFields) => {
    // console.log(key, focus, postFields);
    let output = "";
    if (!key) return "";
    if (focus[key]) output += "focused";
    if (postFields[key]) output += " filled";
    return output;
};

export const checkPhone = (
    phone = "",
    fields,
    errors,
    setErrors,
    name = "phone",
) => {
    let v = phone;
    if (v === "") {
        setErrors({ ...errors, [name]: "short" });
        return false;
    } else {
        v = v.replace(/\D/g, "");
    }
    if (
        v.length > MAX_PHONE_NUMBER_LENGTH ||
        v.length < MIN_PHONE_NUMBER_LENGTH
    ) {
        setErrors({ ...errors, [name]: "short" });
        return false;
    } else {
        setErrors({ ...errors, [name]: "valid" });
        return true;
    }
};

export const clearInputState = (defFields, setErrors) => {
    setErrors({ ...defFields });
};

export const checkEmailValidity = (
    email = "",
    errors,
    setErrors,
    id = "email",
) => {
    const empty = email?.trim().length === 0;
    const valid = EMAIL_REGEXP.test(email);
    if (empty) setErrors({ ...errors, [id]: "empty" });
    else if (!valid) setErrors({ ...errors, [id]: "invalid" });
    else setErrors({ ...errors, [id]: "valid" });
    return !empty && valid;
};

/** Only called from checkEmail, with the address it has already validated. */
export const preCheckEmail = async (email, fields, errors, setErrors) => {
    try {
        // Use Next.js API instead of legacy RPC
        const response = await fetch('/api/user/check-email', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
        });

        const data = await response.json();

        if (data.success && data.exists) {
            // Email is already taken
            setErrors({ ...errors, email: "taken" });
            if (checkPhone(fields.phone, fields, errors, setErrors)) {
                changeHash("mobile-otp");
            } else {
                changeHash("mobile");
            }
            return false;
        }

        if (data.success && data.available) {
            // Email is available
            setErrors({ ...errors, email: "valid" });
            return true;
        }

        return false;
    } catch (error) {
        trackError(asError(error), undefined, { action: 'email-check', component: 'utils' });
        return false;
    }
};

export const checkEmail = async (fields, errors, setErrors, id = "email") => {
    const value = fields[id];
    if (checkEmailValidity(value, errors, setErrors)) {
        const exists = await preCheckEmail(value, fields, errors, setErrors);
        return exists && checkEmailValidity(value, errors, setErrors);
    }
    return false;
};

export const checkLength = (fields, errors, setErrors, key, min = 3) => {
    if (!fields[key] || (fields[key] + "").trim().length < min) {
        const e = errors;
        e[key] = "short";
        setErrors(e);
        return false;
    } else {
        const e = errors;
        e[key] = "valid";
        setErrors(e);
        return true;
    }
};

/** UPS and USPS refuse a parcel with any side longer than this, in inches. */
export const MAX_PARCEL_SIDE_INCHES = 108;

/**
 * `max`, when given, refuses a larger number with the "maxed" state, which is
 * what shows the field's `shipping-max-error` message. The shipping form always
 * passed 108 here, but the function took no fifth argument, so a 200" side was
 * saved without a word (e2e on staging, 2026-10-19).
 */
export const checkNumber = (fields, errors, setErrors, key, required, max = undefined) => {
    if (
        (required && !(fields[key] + "").length) ||
        ((fields[key] + "").length && isNaN(parseFloat(fields[key]))) ||
        parseFloat(fields[key]) == 0.0
    ) {
        setErrors((prev) => ({ ...prev, [key]: "invalid" }));
        return false;
    } else if (max !== undefined && parseFloat(fields[key]) > max) {
        setErrors((prev) => ({ ...prev, [key]: "maxed" }));
        return false;
    } else {
        setErrors((prev) => ({ ...prev, [key]: "valid" }));
        return true;
    }
};

export const checkName = (fields, errors, setErrors, id = "name") => {
    if (!fields) return true;
    if (!fields[id] || (fields[id] + "").trim().length < 2) {
        setErrors({ ...errors, [id]: "invalid" });
        return false;
    } else {
        setErrors({ ...errors, [id]: "valid" });
        return true;
    }
};

/** Whether the blob handed to resize came straight off the input or out of the HEIC converter. */
export type UploadSource = "direct" | "heic-converted";

/**
 * What the error row needs in order to say *why* a photo would not attach.
 * Deliberately excludes the file name: it is user data and the extension is the
 * only part of it that diagnoses anything.
 */
export const describeUpload = (file: unknown, source: UploadSource, extra: Record<string, unknown> = {}) => {
    const asFile = file as Partial<File> | undefined | null;
    const name = typeof asFile?.name === "string" ? asFile.name : "";
    const dot = name.lastIndexOf(".");
    return {
        component: "utils",
        extra: {
            uploadSource: source,
            fileType: (typeof asFile?.type === "string" && asFile.type) || "unknown",
            fileSize: typeof asFile?.size === "number" ? asFile.size : -1,
            fileExtension: dot > -1 ? name.slice(dot + 1).toLowerCase() : "none",
            ...extra,
        },
    };
};

/**
 * Returns a boolean rather than a type predicate on purpose: narrowing `file` to
 * Blob here would break the untyped hand-off to storeImage further down.
 */
export const isBlobLike = (file: unknown): boolean =>
    typeof Blob !== "undefined" && file instanceof Blob;

const resize = (
    file,
    _state,
    setImageQue,
    setPostFields,
    source: UploadSource,
): Promise<void> => {
    return new Promise((resolve) => {
        // Use getElementById for more reliable element retrieval
        const canvas = document.getElementById("canvas") as HTMLCanvasElement;

        if (!canvas) {
            trackError(new Error('Canvas element not found! Make sure canvas#canvas exists in the DOM'), ErrorSeverity.ERROR, { action: 'resize-image', ...describeUpload(file, source) });
            resolve();
            return;
        }

        // The old heic2any converter could hand back undefined or an array of blobs.
        // Either made createObjectURL throw, which rejected out of handleFiles and
        // took the rest of the batch with it. convertHeicToJpeg now always returns
        // a Blob, but keep the guard: name a bad input instead, and keep going.
        if (!isBlobLike(file)) {
            trackError(new Error('Image resize received a non-Blob file'), ErrorSeverity.ERROR, { action: 'resize-image-input', ...describeUpload(file, source, { receivedType: Array.isArray(file) ? 'array' : typeof file }) });
            resolve();
            return;
        }

        const context = canvas.getContext("2d");
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = function () {
            const r = PHOTO_WIDTH / img.naturalWidth;
            canvas.width = PHOTO_WIDTH;
            canvas.height = img.naturalHeight * r;
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
                (blob) => {
                    // toBlob hands back null when the browser cannot encode the
                    // canvas. createObjectURL(null) throws inside this callback, so
                    // the promise never settled and every photo queued behind this
                    // one waited forever. Name it and move on instead.
                    if (!blob) {
                        trackError(new Error('Image resize produced no bytes'), ErrorSeverity.ERROR, { action: 'resize-image-encode', ...describeUpload(file, source) });
                        resolve();
                        return;
                    }
                    const url = URL.createObjectURL(blob);
                    // Use functional update to ensure we get the latest state
                    setImageQue(prevIq => {
                        const newIq = [...prevIq, url];
                        return newIq;
                    });
                    setPostFields(prevFields => ({
                        ...prevFields,
                        photosChanged: true
                    }));
                    // Save the resized bytes the page shows, keyed to this URL,
                    // so a delete or edit later finds this exact photo.
                    storeImage(blob, url);
                    resolve(); // Signal completion after blob is added to queue
                },
                IMAGE_TYPE,
                IMAGE_COMPRESSION,
            );
        };
        img.onerror = () => {
            trackError(new Error('Failed to load image'), ErrorSeverity.ERROR, { action: 'resize-image-load', ...describeUpload(file, source) });
            resolve(); // Still resolve to continue with other images
        };
    });
};

export const handleFiles = async (
    files,
    state,
    setImageQue,
    setPostFields,
    _iq,
    _i,
) => {
    // Process files sequentially to avoid canvas race condition
    // Each image needs exclusive access to the shared canvas during resize
    const filesArray = Array.from(files);
    for (const file of filesArray as File[]) {
        // One unreadable photo must not cancel the ones queued behind it — before
        // this, a throw anywhere in the conversion abandoned the rest of the batch
        // and, because inputHandler calls this with `void`, said nothing at all.
        const source: UploadSource = isHeicFile(file) ? "heic-converted" : "direct";
        try {
            const processedFile = source === "heic-converted" ? await convertHeicToJpeg(file) : file;
            await resize(processedFile, state, setImageQue, setPostFields, source);
        } catch (err) {
            trackError(asError(err), ErrorSeverity.ERROR, { action: 'handle-files-item', ...describeUpload(file, source) });
        }
    }
};

/** Told how many picked photos were kept and how many did not fit. */
export type PhotoOverflowHandler = (added: number, dropped: number) => void;

/** The words for a pick that went past the cap. */
export const photoLimitMessage = (added: number, dropped: number): string =>
    added === 0
        ? photoLimitNoneAdded
        : photoLimitSomeAdded
            .replace("{added}", String(added))
            .replace("{total}", String(added + dropped));

/** Default overflow report: a notice, so the photos past the cap are not dropped in silence. */
export const notifyPhotoOverflow: PhotoOverflowHandler = (added, dropped) => {
    createNotification({ type: "warning", msg: photoLimitMessage(added, dropped), dismissed: false });
};

/**
 * Keep the picked files that fit under MAX_PHOTOS, given `existing` photos
 * already on the listing. Picking 25 at once, or 5 with 18 already there, used
 * to keep what fit and drop the rest without a word; now `onOverflow` hears
 * how many were kept and how many were left out.
 */
export const limitPhotosToSlots = <T,>(
    files: T[],
    existing: number,
    onOverflow: PhotoOverflowHandler = notifyPhotoOverflow,
): T[] => {
    const left = Math.max(0, MAX_PHOTOS - existing);
    const kept = files.slice(0, left);
    if (files.length > kept.length) onOverflow(kept.length, files.length - kept.length);
    return kept;
};

export const inputHandler = (ev, state, setImageQue, setPostFields, onOverflow: PhotoOverflowHandler = notifyPhotoOverflow) => {
    // Use the event target to get the correct fileInput that triggered the event
    const fileInput = ev.target;

    if (!fileInput || !fileInput.files) {
        trackError(new Error('No files on event target'), undefined, { action: 'input-handler', component: 'utils' });
        return;
    }

    const iq = state.postImageQue;
    const length = iq.filter((t) => !!t).length;
    const files = limitPhotosToSlots(Array.from(fileInput.files), length, onOverflow);
    if (!files.length) {
        return;
    }
    void handleFiles(files, state, setImageQue, setPostFields, iq, 0);
};

export const setupFileUpload = (_state, _setImageQue, _setPostFields) => {
    const fileInputs = $(".fileInput");
    const elements = Array.isArray(fileInputs) ? fileInputs : [fileInputs];

    if (!elements || elements.length === 0) {
        return;
    }

    elements.forEach((fileInput) => {
        if (fileInput && fileInput.addEventListener) {
            fileInput.addEventListener("click", (e) => {
                e.stopPropagation();
            });
        }
    });
};

export const unreadTransactionsAsc = (a, b, bs) => {
    if (a[bs].read < b[bs].read) {
        return -1;
    }
    if (a[bs].read > b[bs].read) {
        return 1;
    }
    return 0;
};

export const lastModifiedDesc = (a, b) => {
    if (a.lastModified < b.lastModified) {
        return 1;
    }
    if (a.lastModified > b.lastModified) {
        return -1;
    }
    return 0;
};

export const getCountries = (setCountries = null) => {
    /* HARD CODED bc we only allow US/Canada based addresses for now.
    const response = await post(
      COUNTRY_API_ENDPOINT(),
      getAllCountriesParams()
    );
    const a = response.result.rs;
    */
    const a = [
        {
            id: 237,
            status: 1,
            name: "United States of America",
            isoTwoLetter: "US",
            isoThreeLetter: "USA",
        },
        {
            id: 41,
            status: 1,
            name: "Canada",
            isoTwoLetter: "CA",
            isoThreeLetter: "CAN",
        },
    ];
    if (setCountries) setCountries(a);
    return a;
};

export const requireConfirmWhenSaving = (router, saving) => {
    if (!saving) return () => {};

    const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = unloadConfirmMessage;
        return unloadConfirmMessage;
    };
    const beforeRouteHandler = (url) => {
        if (router.pathname !== url && !confirm(unloadConfirmMessage)) {
            router.events.emit("routeChangeError");
            throw "";
        }
    };

    router.events.on("routeChangeStart", beforeRouteHandler);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
        router.events.off("routeChangeStart", beforeRouteHandler);
        window.removeEventListener("beforeunload", handleBeforeUnload);
    };
};

export const isVehicle = (postFields) => {
    // Safety check for undefined or missing categoryId
    if (!postFields || !postFields.categoryId) {
        return false;
    }
    return VEHICLE_SUBCATS.includes(parseInt(postFields.categoryId));
};

export const checkMileage = (fields, errors, setErrors) => {
    // console.log(fields);
    return isVehicle(fields)
        ? checkNumber(fields, errors, setErrors, "mileage", isVehicle(fields))
        : true;
};

export const isValidVIN = (vin) => {
    if (!vin) {// || vin.length > 17 || vin.length < 11) {
      return false;
    }
    else {
      return true;
    }

    // // Check for invalid characters
    // if (/[^A-HJ-NPR-Z0-9]/.test(vin)) {
    //   return false;
    // }

    // // Compute the check digit
    // const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
    // const transliterations = {
    //   A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
    //   J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
    //   S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
    //   0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9
    // };

    // let sum = 0;
    // for (let i = 0; i < 17; i++) {
    //   const char = vin[i];
    //   sum += (transliterations[char] || 0) * weights[i];
    // }

    // const checkDigit = sum % 11;
    // const vinCheckDigit = vin[8] === 'X' ? 10 : parseInt(vin[8], 10);

    // return checkDigit === vinCheckDigit;
  }
